import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import SalesLastMonth from './panels/SalesLastMonth';
import TopSalesProduct from './panels/TopSalesProduct';
import TopSalesCategory from './panels/TopSalesCategory';
import DashboardHeader from './DashboardHeader';
import { DASHBOARD_MACHINE_ID } from '../../../actions/utils';
import withStyles from '@material-ui/core/styles/withStyles';
import {
  BACKGROUND_DARK,
  BORDER_DARK,
  DASHBOARD_BOX_SHADOW, DASHBOARD_BOX_SHADOW_DARK,
  VERY_LIGHT_GREY_COLOR,
} from '../../../const/color-const';
import { DASHBOARD_RADIUS } from '../../../const/chart-const';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import CommonSnackbar, { NOTIFICATION_TYPE } from '../../common/CommonSnackbar';
import { LOCAL_STORAGE_TAX_POPUP } from '../../../actions/auth-actions';
import LastDaysSales from './panels/LastDaysSales';

const styles = (theme) => ({
  homepage: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    overflow: 'hidden',
    fontFamily: 'Gilroy, sans-serif',
    letterSpacing: '0.1px',
    fontWeight: 500,
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 2.5, 2.5, 2.5),
      width: `calc(100vw - 280px)`,
    },
  },
  contentTitle: {
    width: '100%',
    textAlign: 'center',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  smallerCharts: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  mainPartWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  chartCard: {
    width: 300,
    margin: theme.spacing(0, 0, 4, 0),
    boxShadow: theme.palette.type === 'light' ? DASHBOARD_BOX_SHADOW : DASHBOARD_BOX_SHADOW_DARK,
    background: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      width: '32.5%',
      borderRadius: DASHBOARD_RADIUS,
      margin: theme.spacing(0, 0, 4, 0),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
    boxShadow: theme.palette.type === 'light' ? DASHBOARD_BOX_SHADOW : DASHBOARD_BOX_SHADOW_DARK,
    border: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `1px solid ${BORDER_DARK}`,
    borderRadius: DASHBOARD_RADIUS,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  loading: {
    height: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 40,
    width: '32.5%',
  },
});

const Dashboard = (props) => {
  const { machines, classes } = props;
  const { formatMessage } = useIntl();
  
  const machinesOptions = [
    { label: formatMessage({ id: 'all.cash.registers', defaultMessage: 'All cash registers' }), value: null },
    ...machines.map(machine => ({ label: machine.Name, value: machine.Id })),
  ];
  
  const allMachineIDs = machines.map(machine => machine.Id);
  const storedMachineIds = localStorage.getItem(DASHBOARD_MACHINE_ID);
  const initialSelectedMachineIds = storedMachineIds ? JSON.parse(storedMachineIds) : [machinesOptions[0].value];
  
  const [selectedMachineIds, setSelectedMachineIds] = useState(initialSelectedMachineIds);
  const [fetchingStateArray, setFetchingStateArray] = useState([]);
  
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const isVisible = localStorage.getItem(LOCAL_STORAGE_TAX_POPUP);
  
  useEffect(() => {
    if (!isVisible) {
      localStorage.setItem(LOCAL_STORAGE_TAX_POPUP, 'true');
      setIsSnackbarVisible(true);
    }
  }, [isVisible]);
  
  const handleSnackbarClose = () => {
    setIsSnackbarVisible(false);
  };
  
  return (
    <div className={classes.homepage}>
      <DashboardHeader
        machinesOptions={machinesOptions}
        setSelectedMachineIds={setSelectedMachineIds}
        initialSelectedMachineIds={initialSelectedMachineIds}
        fetchingStateArray={fetchingStateArray}
      />
  
      <div className={classes.panelWrapper}>
        <SalesLastMonth styles={classes}
                        allMachineIDs={allMachineIDs}
                        selectedMachineIds={selectedMachineIds}
                        setFetchingStateArray={setFetchingStateArray}
        />
    
        <LastDaysSales styles={classes}
                       selectedMachineIds={selectedMachineIds}
        />
    
        <TopSalesProduct styles={classes}
                         allMachineIDs={allMachineIDs}
                         selectedMachineIds={selectedMachineIds}
                         setFetchingStateArray={setFetchingStateArray}
        />
    
        <TopSalesCategory styles={classes}
                          allMachineIDs={allMachineIDs}
                          selectedMachineIds={selectedMachineIds}
                          setFetchingStateArray={setFetchingStateArray}
        />
      </div>
      
      {isSnackbarVisible && <CommonSnackbar
        open={true}
        title={<FormattedMessage id="notification" defaultMessage="Notification" />}
        message={<FormattedMessage id="notification.bar.tax"
                                   defaultMessage="Remember to report your cash register to Skatteverket!" />}
        snackbarType={NOTIFICATION_TYPE}
        onExtraClose={handleSnackbarClose}
        timeframe={8000}
      />}
    </div>
  );
};

const decorate = compose(
  withStyles(styles),
  connect(
    store => ({
      machines: store.user.machines,
      dashboardReports: store.report.dashboard,
    }),
  ),
);

Dashboard.propTypes = {
  machines: PropTypes.array,
  classes: PropTypes.object,
};

export default decorate(Dashboard);
